<template>
  <v-container fluid>
    <!-- Verificación de permisos -->
    <div v-if="!canManageClients">
      <v-alert type="error" prominent border="left">
        <v-row no-gutters>
          <v-col class="grow">
            <div class="title">🚫 Acceso Restringido</div>
            <div>
              Solo los <strong>Administradores</strong> pueden gestionar clientes de la
              tienda.
              <br />
              <small>Contacte al gerente para obtener permisos adicionales.</small>
            </div>
          </v-col>
          <v-col class="shrink">
            <v-icon color="error" size="48">mdi-shield-alert</v-icon>
          </v-col>
        </v-row>
      </v-alert>
    </div>

    <!-- Contenido principal -->
    <div v-else>
      <!-- KPI Dashboard -->
      <v-row class="mb-4">
        <v-col cols="12" sm="6" md="3">
          <v-card
            class="mx-1 kpi-card"
            elevation="2"
            :style="{ borderTop: '4px solid #1976D2' }"
          >
            <v-card-text class="text-center py-4">
              <div class="text-overline mb-1">Total Clientes</div>
              <div class="text-h5 font-weight-bold mb-2">
                {{ clientStats.total_clients || 0 }}
              </div>
              <div class="text-body-2">
                <v-icon class="mr-2" color="primary" size="20">mdi-account-group</v-icon>
                <span>{{ getCurrentStoreName() }}</span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card
            class="mx-1 kpi-card"
            elevation="2"
            :style="{ borderTop: '4px solid #4CAF50' }"
          >
            <v-card-text class="text-center py-4">
              <div class="text-overline mb-1">Clientes Activos</div>
              <div class="text-h5 font-weight-bold mb-2">
                {{ clientStats.active_clients || 0 }}
              </div>
              <div class="text-body-2">
                <span class="success--text font-weight-medium">
                  <v-icon small color="success" class="mr-1">mdi-check-circle</v-icon>
                  {{ activePeriod }}
                </span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card
            class="mx-1 kpi-card"
            elevation="2"
            :style="{ borderTop: '4px solid #FF9800' }"
          >
            <v-card-text class="text-center py-4">
              <div class="text-overline mb-1">Nuevos Este Mes</div>
              <div class="text-h5 font-weight-bold mb-2">
                {{ clientStats.new_clients || 0 }}
              </div>
              <div class="text-body-2">
                <v-icon class="mr-2" color="orange" size="20">mdi-account-plus</v-icon>
                <span>{{ getCurrentMonth() }}</span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card
            class="mx-1 kpi-card"
            elevation="2"
            :style="{ borderTop: '4px solid #9C27B0' }"
          >
            <v-card-text class="text-center py-4">
              <div class="text-overline mb-1">Identificados</div>
              <div class="text-h5 font-weight-bold mb-2">
                {{ clientStats.identified_clients || 0 }}
              </div>
              <div class="text-body-2">
                <v-icon class="mr-2" color="purple" size="20"
                  >mdi-card-account-details</v-icon
                >
                <span>Con datos completos</span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Toolbar con controles -->
      <v-card class="mb-4 elevation-2">
        <v-toolbar flat color="white" class="px-2">
          <v-toolbar-title class="text-h6 primary--text">
            <v-icon color="primary" class="mr-2">mdi-account-group</v-icon>
            Gestión de Clientes
          </v-toolbar-title>
          <v-spacer></v-spacer>

          <!-- Filtro por tipo -->
          <v-select
            v-model="selectedTypeFilter"
            :items="typeFilterOptions"
            label="Tipo de cliente"
            dense
            outlined
            hide-details
            clearable
            class="mx-2 shrink"
            style="max-width: 200px"
          >
            <template v-slot:prepend-inner>
              <v-icon size="18">mdi-filter</v-icon>
            </template>
          </v-select>

          <!-- Búsqueda -->
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Buscar cliente"
            hint="Nombre, documento, email o teléfono"
            persistent-hint
            hide-details
            dense
            outlined
            class="mx-2 shrink"
            style="max-width: 300px"
          ></v-text-field>

          <!-- Botón de búsqueda -->
          <v-btn
            color="primary"
            class="ml-2"
            @click="searchClients"
            :loading="loading_status"
            outlined
          >
            <v-icon left>mdi-magnify</v-icon>
            Buscar
          </v-btn>

          <!-- Botón nuevo cliente -->
          <v-btn color="primary" class="ml-2" @click="newClient" depressed>
            <v-icon left>mdi-account-plus</v-icon>
            Nuevo Cliente
          </v-btn>
        </v-toolbar>
      </v-card>

      <!-- Tabla de Clientes -->
      <v-card class="elevation-2">
        <v-data-table
          :headers="headers"
          :items="clients"
          :items-per-page="15"
          :loading="loading_status"
          class="table-cursor"
          sort-by="PartyName"
          item-key="PartyIdentification"
          @click:row="editClient"
          :footer-props="{
            'items-per-page-options': [15, 30, 50, 100],
            'items-per-page-text': 'Clientes por página:',
          }"
        >
          <template v-slot:item.client_avatar="{ item }">
            <v-avatar
              size="40"
              :color="getClientTypeColor(item.tipo_cliente)"
              class="white--text"
            >
              <v-icon color="white">{{ getClientTypeIcon(item.tipo_cliente) }}</v-icon>
            </v-avatar>
          </template>

          <template v-slot:item.client_info="{ item }">
            <div>
              <div class="font-weight-medium">{{ item.PartyName }}</div>
              <div class="caption grey--text">
                <v-icon small class="mr-1">mdi-card-account-details</v-icon>
                {{ item.PartyIdentification }}
              </div>
              <div v-if="item.client_email" class="caption grey--text">
                <v-icon small class="mr-1">mdi-email-outline</v-icon>
                {{ item.client_email }}
              </div>
            </div>
          </template>

          <template v-slot:item.contact_info="{ item }">
            <div>
              <div v-if="item.client_phone" class="caption">
                <v-icon small class="mr-1">mdi-phone</v-icon>
                {{ item.client_phone }}
              </div>
              <div v-if="item.AddressLine" class="caption grey--text">
                <v-icon small class="mr-1">mdi-map-marker</v-icon>
                {{ item.AddressLine }}
              </div>
              <div v-if="item.CityName" class="caption grey--text">
                <v-icon small class="mr-1">mdi-city</v-icon>
                {{ item.CityName }}
              </div>
            </div>
          </template>

          <template v-slot:item.tipo_cliente="{ item }">
            <v-chip
              :color="getClientTypeColor(item.tipo_cliente)"
              text-color="white"
              class="font-weight-bold"
              small
            >
              <v-icon left small>{{ getClientTypeIcon(item.tipo_cliente) }}</v-icon>
              {{ getClientTypeText(item.tipo_cliente) }}
            </v-chip>
          </template>

          <template v-slot:item.document_type="{ item }">
            <v-chip
              :color="getDocumentTypeColor(item.typeid)"
              text-color="white"
              class="font-weight-bold"
              small
            >
              {{ item.typeid || "DNI" }}
            </v-chip>
          </template>

          <template v-slot:item.channel="{ item }">
            <v-chip
              :color="getChannelColor(item.channel)"
              text-color="white"
              class="font-weight-bold"
              small
            >
              <v-icon left small>{{ getChannelIcon(item.channel) }}</v-icon>
              {{ getChannelText(item.channel) }}
            </v-chip>
          </template>

          <template v-slot:item.created_at="{ item }">
            <div class="text-caption">
              <v-icon small class="mr-1">mdi-calendar</v-icon>
              {{ formatDate(item.created_at) }}
            </div>
          </template>

          <template v-slot:no-data>
            <div class="text-center pa-4">
              <v-icon size="48" color="grey lighten-1" class="mb-2"
                >mdi-account-off</v-icon
              >
              <div class="subtitle-1 grey--text">No hay clientes registrados</div>
              <div class="caption grey--text mb-4">
                Utilice el botón "Nuevo Cliente" para agregar el primer cliente
              </div>
              <v-btn color="primary" @click="newClient">
                <v-icon left>mdi-account-plus</v-icon>
                Agregar Primer Cliente
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-card>

      <!-- Diálogo de Cliente -->
      <v-dialog v-model="dialog" max-width="800" scrollable persistent>
        <v-card>
          <v-toolbar flat color="primary" dark>
            <v-icon color="white" class="mr-2">
              {{ newItem ? "mdi-account-plus" : "mdi-pencil" }}
            </v-icon>
            <v-toolbar-title>
              {{ newItem ? "Nuevo Cliente" : "Editar Cliente" }}
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon @click="closeDialog">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>

          <v-card-text class="pt-4">
            <v-form ref="clientForm" v-model="formValid" lazy-validation>
              <!-- Información básica -->
              <v-row>
                <v-col cols="12" md="8">
                  <v-text-field
                    v-model="item.PartyName"
                    label="Nombre completo *"
                    prepend-icon="mdi-account"
                    :rules="[rules.required]"
                    required
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-select
                    v-model="item.typeid"
                    :items="documentTypes"
                    label="Tipo de documento *"
                    prepend-icon="mdi-card-account-details"
                    :rules="[rules.required]"
                    required
                    @change="onDocumentTypeChange"
                  >
                    <template v-slot:prepend-item>
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-subtitle class="text-center">
                            {{ getCurrentCountryConfig().flag }}
                            {{ getCurrentCountryConfig().name }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                      <v-divider></v-divider>
                    </template>
                  </v-select>
                </v-col>
              </v-row>

              <!-- Documento de identidad -->
              <v-row>
                <v-col cols="12" md="8">
                  <v-text-field
                    v-model="item.PartyIdentification"
                    label="Número de documento *"
                    prepend-icon="mdi-identifier"
                    :rules="[rules.required, rules.documentFormat]"
                    :hint="getDocumentHint()"
                    persistent-hint
                    required
                    @input="validateDocument"
                    @blur="searchExistingClient"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-btn
                    @click="validateDocumentManually"
                    :color="documentValidation.valid ? 'success' : 'error'"
                    :loading="validatingDocument"
                    outlined
                    block
                  >
                    <v-icon left>
                      {{ documentValidation.valid ? "mdi-check" : "mdi-alert" }}
                    </v-icon>
                    {{ documentValidation.valid ? "Válido" : "Validar" }}
                  </v-btn>
                </v-col>
              </v-row>

              <!-- Información de contacto -->
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="item.client_email"
                    label="Email"
                    prepend-icon="mdi-email"
                    :rules="[rules.email]"
                    type="email"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="item.client_phone"
                    label="Teléfono"
                    prepend-icon="mdi-phone"
                    :rules="[rules.phone]"
                  ></v-text-field>
                </v-col>
              </v-row>

              <!-- Dirección -->
              <v-row>
                <v-col cols="12" md="8">
                  <v-text-field
                    v-model="item.AddressLine"
                    label="Dirección completa"
                    prepend-icon="mdi-map-marker"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="item.CityName"
                    label="Ciudad"
                    prepend-icon="mdi-city"
                  ></v-text-field>
                </v-col>
              </v-row>

              <!-- Tipo de cliente y Canal -->
              <v-row>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="item.tipo_cliente"
                    :items="clientTypes"
                    label="Tipo de cliente *"
                    prepend-icon="mdi-account-group"
                    :rules="[rules.required]"
                    required
                  ></v-select>
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="item.channel"
                    :items="channelOptions"
                    label="Canal de origen"
                    prepend-icon="mdi-source-branch"
                    item-text="text"
                    item-value="value"
                  >
                    <template v-slot:selection="{ item }">
                      <v-icon small class="mr-2">{{ getChannelIcon(item.value) }}</v-icon>
                      {{ item.text }}
                    </template>
                    <template v-slot:item="{ item }">
                      <v-icon small class="mr-2" :color="getChannelColor(item.value)">
                        {{ getChannelIcon(item.value) }}
                      </v-icon>
                      {{ item.text }}
                    </template>
                  </v-select>
                </v-col>
              </v-row>

              <!-- Checkbox cliente genérico -->
              <v-row>
                <v-col cols="12">
                  <v-checkbox
                    v-model="item.es_generico"
                    label="Cliente genérico"
                    hint="Para uso interno del sistema"
                    persistent-hint
                  ></v-checkbox>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>

          <v-card-actions class="pa-4">
            <v-btn
              v-if="!newItem && !isGenericClient(item.es_generico)"
              color="error"
              text
              @click="deleteClient(item)"
            >
              <v-icon left>mdi-delete</v-icon>
              Eliminar
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              @click="saveClient"
              :loading="saving"
              :disabled="!formValid || !documentValidation.valid"
            >
              <v-icon left>mdi-content-save</v-icon>
              {{ newItem ? "Crear" : "Guardar" }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Diálogo de confirmación -->
      <v-dialog v-model="confirmDialog" max-width="400">
        <v-card>
          <v-card-title class="headline">
            <v-icon left :color="confirmAction.color">{{ confirmAction.icon }}</v-icon>
            {{ confirmAction.title }}
          </v-card-title>
          <v-card-text>
            {{ confirmAction.message }}
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="confirmDialog = false">Cancelar</v-btn>
            <v-btn
              :color="confirmAction.color"
              @click="executeConfirmAction"
              :loading="loading_status"
            >
              {{ confirmAction.confirmText }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </v-container>
</template>

<script>
import { webserver } from "@/services/webserver.js";
import createClient from "@/utils/createClient.js";
import countryUtils from "@/utils/countryUtils.js";

export default {
  name: "ClientsManagement",

  mounted() {
    this.checkPermissions();
    if (this.canManageClients) {
      // Cargar configuraciones dinámicas según país (como en Company.vue)
      this.loadDynamicSettings();
      this.getClientStats();
      this.initializeClients();
      this.loadAllClients();
    }

    // Escuchar cambios de país para actualizar configuraciones
    window.addEventListener("countrySettingsChanged", this.handleCountryChange);
  },

  beforeDestroy() {
    window.removeEventListener("countrySettingsChanged", this.handleCountryChange);
  },

  data() {
    return {
      // Permisos
      canManageClients: false,
      currentUserAccess: null,

      // Estado
      loading_status: false,
      saving: false,
      validatingDocument: false,
      dialog: false,
      confirmDialog: false,
      newItem: false,
      formValid: true,

      // Datos
      clients: [],
      clientStats: {
        total_clients: 0,
        active_clients: 0,
        new_clients: 0,
        identified_clients: 0,
        generic_clients: 0,
      },
      activePeriod: "Mes actual", // Período para clientes activos
      item: createClient(),
      search: "",
      selectedTypeFilter: null,

      // Validación de documento
      documentValidation: {
        valid: false,
        type: null,
        message: "",
      },

      // Confirmación
      confirmAction: {
        title: "",
        message: "",
        icon: "",
        color: "",
        confirmText: "",
        action: null,
        data: null,
      },

      // Configuración
      clientTypes: [
        { text: "Identificado", value: "identificado" },
        { text: "Contado", value: "contado" },
        { text: "Consumidor", value: "consumidor" },
        { text: "Extranjero", value: "extranjero" },
      ],

      // Opciones de canales
      channelOptions: [
        { text: "Punto de Venta", value: "POS" },
        { text: "WhatsApp", value: "WHATSAPP" },
        { text: "Sitio Web", value: "WEB" },
        { text: "Aplicación Móvil", value: "APP" },
        { text: "Instagram/Facebook", value: "INSTAGRAM" },
        { text: "Llamada Telefónica", value: "PHONE" },
        { text: "Email", value: "EMAIL" },
        { text: "Marketplace", value: "MARKETPLACE" },
        { text: "Importación", value: "IMPORT" },
        { text: "API Externa", value: "API" },
      ],

      documentTypes: [], // Será cargado dinámicamente según país del perfil
      defaultDocumentType: "DNI", // Tipo por defecto según país
      countryConfig: null, // Configuración del país actual

      typeFilterOptions: [
        { text: "Todos los tipos", value: null },
        { text: "Identificados", value: "identificado" },
        { text: "Contado", value: "contado" },
        { text: "Consumidor", value: "consumidor" },
        { text: "Extranjero", value: "extranjero" },
      ],

      headers: [
        { text: "", value: "client_avatar", sortable: false, width: "60" },
        { text: "Cliente", value: "client_info", sortable: true },
        { text: "Contacto", value: "contact_info", sortable: false },
        { text: "Tipo", value: "tipo_cliente", sortable: true, width: "140" },
        { text: "Documento", value: "document_type", sortable: true, width: "100" },
        { text: "Canal", value: "channel", sortable: true, width: "100" },
        { text: "Registro", value: "created_at", sortable: true, width: "120" },
      ],

      // Reglas de validación
      rules: {
        required: (value) => !!value || "Este campo es obligatorio",
        email: (value) => {
          if (!value) return true;
          const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          return pattern.test(value) || "Email inválido";
        },
        phone: (value) => {
          if (!value) return true;
          const pattern = /^[0-9+\-\s()]+$/;
          return pattern.test(value) || "Teléfono inválido";
        },
        documentFormat: (value) => {
          if (!value) return "Documento es obligatorio";
          if (!this.documentValidation.valid && this.documentValidation.message) {
            return this.documentValidation.message;
          }
          return this.documentValidation.valid || "Documento inválido";
        },
      },
    };
  },

  computed: {
    filteredClients() {
      return this.clients;
    },
  },

  methods: {
    /**
     * Carga configuraciones dinámicas según el país actual (como en Company.vue)
     */
    loadDynamicSettings() {
      try {
        console.log("🌍 Cargando configuraciones dinámicas para Clientes...");

        // Obtener tipos de documento del país actual
        const documentTypes = countryUtils.getDocumentTypes();
        const countryCode = countryUtils.getCurrentCountryCode();

        // Convertir al formato esperado por el componente
        this.documentTypes = documentTypes.map((doc) => ({
          text: `${doc.code} - ${doc.name}`,
          value: doc.code,
          country: countryCode,
          validation: doc.validation || null,
        }));

        // Establecer tipo de documento por defecto según país
        this.defaultDocumentType = this.getDefaultDocumentTypeForCountry();

        console.log("✅ Configuraciones dinámicas cargadas para Clientes:", {
          country: countryCode,
          documentsCount: this.documentTypes.length,
          defaultType: this.defaultDocumentType,
        });
      } catch (error) {
        console.error("❌ Error cargando configuraciones dinámicas:", error);
        // Fallback a configuración española
        this.documentTypes = [
          { text: "DNI - Documento Nacional de Identidad", value: "DNI" },
          { text: "NIE - Número de Identidad de Extranjero", value: "NIE" },
          { text: "CIF - Código de Identificación Fiscal", value: "CIF" },
          { text: "PAS - Pasaporte", value: "PAS" },
        ];
        this.defaultDocumentType = "DNI";
      }
    },

    /**
     * Maneja el cambio de país (escucha eventos como Company.vue)
     */
    handleCountryChange(event) {
      console.log("🔄 Detectado cambio de país en Clientes:", event.detail);
      this.loadDynamicSettings();
      this.showSuccess(
        `Configuración actualizada para ${event.detail.config.country.name}`
      );
    },

    getCurrentStoreName() {
      const currentUser = this.$store?.state?.profile || window.profile;
      return currentUser?.store_name || window.profile?.store_name || "Mi Tienda";
    },

    getCurrentMonth() {
      const now = new Date();
      const months = [
        "Enero",
        "Febrero",
        "Marzo",
        "Abril",
        "Mayo",
        "Junio",
        "Julio",
        "Agosto",
        "Septiembre",
        "Octubre",
        "Noviembre",
        "Diciembre",
      ];
      return months[now.getMonth()];
    },

    getStoreId() {
      return window.profile?.store_id || this.$store?.state?.profile?.store_id;
    },

    checkPermissions() {
      const currentUser = this.$store?.state?.profile || window.profile;

      if (!currentUser) {
        this.canManageClients = false;
        return;
      }

      this.currentUserAccess = currentUser.user_access;
      this.canManageClients = ["ADMIN", "POS", "VENDEDOR"].includes(
        this.currentUserAccess
      );
    },

    // Cargar todos los clientes automáticamente
    loadAllClients() {
      this.loading_status = true;
      const storeId = this.getStoreId();

      const qry = {
        store: storeId,
        search: "", // Búsqueda vacía para obtener todos
        limit: 1000, // Límite alto para obtener todos los clientes
      };

      webserver("search_clients", qry, (response) => {
        this.loading_status = false;

        if (Array.isArray(response)) {
          this.clients = response;
        } else if (response && response.error) {
          console.error("Error al cargar clientes:", response.error);
          this.showError("Error al cargar clientes: " + response.error);
          this.clients = [];
        } else {
          this.clients = [];
        }
      });
    },

    // Obtener código de país usando countryUtils (como en Company.vue)
    getCountryCode() {
      return countryUtils.getCurrentCountryCode();
    },

    // Buscar cliente existente por documento
    searchExistingClient() {
      if (!this.item.PartyIdentification || this.item.PartyIdentification.length < 5) {
        return;
      }

      const storeId = this.getStoreId();
      const qry = {
        store: storeId,
        PartyIdentification: this.item.PartyIdentification.toUpperCase(),
      };

      console.log("🔍 Buscando cliente existente:", qry);

      webserver("get_client_info", qry, (response) => {
        console.log("📥 Cliente existente encontrado:", response);

        if (response && response.PartyIdentification) {
          // Cliente encontrado, preguntar si quiere cargarlo
          this.confirmLoadExistingClient(response);
        }
      });
    },

    // Confirmar carga de cliente existente
    confirmLoadExistingClient(existingClient) {
      this.confirmAction = {
        title: "Cliente Existente",
        message: `Se encontró un cliente con este documento:\n\n¿${existingClient.PartyName}\n\n¿Desea cargar sus datos?`,
        icon: "mdi-account-search",
        color: "info",
        confirmText: "Cargar Datos",
        action: "loadExistingClient",
        data: existingClient,
      };

      this.confirmDialog = true;
    },

    // Cargar datos del cliente existente
    loadExistingClient(client) {
      this.item = {
        ...createClient(),
        ...client,
      };

      // Validar documento automáticamente
      this.validateDocument();

      this.showSuccess(`Datos de ${client.PartyName} cargados correctamente`);
    },

    // Inicializar tabla de clientes
    initializeClients() {
      const storeId = this.getStoreId();

      const qry = { store: storeId };

      webserver("init_clients_table", qry, (response) => {
        if (response && response.success) {
          // Tabla inicializada correctamente
        } else if (response && response.error) {
          console.error("Error al inicializar:", response.error);
        }
      });
    },

    // Buscar clientes
    searchClients() {
      this.loading_status = true;
      const storeId = this.getStoreId();

      const qry = {
        store: storeId,
        search: this.search,
        limit: 100,
      };

      webserver("search_clients", qry, (response) => {
        this.loading_status = false;

        if (Array.isArray(response)) {
          this.clients = response;
        } else if (response && response.error) {
          console.error("Error en búsqueda:", response.error);
          this.showError("Error al buscar clientes: " + response.error);
          this.clients = [];
        } else {
          this.clients = [];
        }
      });
    },

    // Obtener estadísticas
    getClientStats() {
      const storeId = this.getStoreId();

      // Verificar que tenemos store_id
      if (!storeId) {
        this.showError("Error: No se pudo obtener el ID de la tienda");
        return;
      }

      const now = new Date();
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

      const qry = {
        store: storeId,
        date_from: firstDayOfMonth.toISOString().split("T")[0],
        date_to: now.toISOString().split("T")[0],
      };

      webserver("get_client_stats", qry, (response) => {
        // Manejar respuesta de error estructurada
        if (response && response.error === true) {
          this.showError("Error del servidor: " + response.message);

          // Usar valores por defecto
          this.clientStats = {
            total_clients: 0,
            active_clients: 0,
            new_clients: 0,
            identified_clients: 0,
            generic_clients: 0,
          };
          return;
        }

        if (response && !response.error) {
          // Asegurar que todos los campos existen
          this.clientStats = {
            total_clients: response.total_clients || 0,
            active_clients: response.active_clients || 0,
            new_clients: response.new_clients || 0,
            identified_clients: response.identified_clients || 0,
            generic_clients: response.generic_clients || 0,
          };

          // Guardar información del período
          if (response.period_info) {
            this.activePeriod = response.period_info.description;
          }
        } else {
          this.showError("Error al obtener estadísticas de clientes");

          // Usar valores por defecto
          this.clientStats = {
            total_clients: 0,
            active_clients: 0,
            new_clients: 0,
            identified_clients: 0,
            generic_clients: 0,
          };
        }
      });
    },

    // Debug temporal - ahora no se usa
    debugClients() {
      // Método obsoleto - debug ahora está en getClientStats
    },

    // Validar documento localmente primero, luego en servidor
    validateDocument() {
      if (!this.item.PartyIdentification) {
        this.documentValidation = { valid: false, type: null, message: "" };
        return;
      }

      // Validación local usando countryUtils (como Company.vue)
      this.validateDocumentWithCountryUtils();

      // Si queremos también validación en servidor, descomentar:
      // if (this.documentValidation.valid) {
      //   this.validateInServer();
      // }
    },

    // Validación local usando countryUtils
    validateDocumentWithCountryUtils() {
      if (!this.item.PartyIdentification || !this.item.typeid) {
        this.documentValidation = {
          valid: false,
          type: null,
          message: "Documento y tipo son obligatorios",
        };
        return this.documentValidation;
      }

      try {
        const result = countryUtils.validateDocument(
          this.item.typeid,
          this.item.PartyIdentification.toUpperCase()
        );

        this.documentValidation = {
          valid: result.valid,
          type: this.item.typeid,
          message: result.message,
        };

        // No mostrar mensajes automáticos para no ser molesto
        // if (result.valid) {
        //   this.showSuccess(`${this.item.typeid} válido`);
        // }

        return this.documentValidation;
      } catch (error) {
        console.error("Error validando documento:", error);
        this.documentValidation = {
          valid: false,
          type: null,
          message: "Error en validación",
        };
        return this.documentValidation;
      }
    },

    // Validación en servidor (opcional)
    validateInServer() {
      this.validatingDocument = true;

      const qry = {
        document: this.item.PartyIdentification.toUpperCase(),
        document_type: this.item.typeid || "DNI",
        country: this.getCountryCode(),
      };

      console.log("📤 Validando documento en servidor:", qry);

      webserver("validate_document", qry, (response) => {
        this.validatingDocument = false;
        console.log("📥 Respuesta validate_document:", response);

        if (response) {
          this.documentValidation = response;
          if (response.valid) {
            this.showSuccess(`${response.type} válido`);
          } else {
            this.showError(`Documento inválido: ${response.message}`);
          }
        }
      });
    },

    validateDocumentManually() {
      this.validateDocument();
    },

    // Evento cuando cambia el tipo de documento
    onDocumentTypeChange() {
      // Limpiar validación anterior
      this.documentValidation = { valid: false, type: null, message: "" };

      // Si hay un documento ingresado, validar con el nuevo tipo
      if (this.item.PartyIdentification) {
        this.validateDocument();
      }

      console.log("📝 Tipo de documento cambiado a:", this.item.typeid);
    },

    // Obtener información del cliente
    getClientInfo(identification) {
      const storeId = this.getStoreId();

      const qry = {
        store: storeId,
        PartyIdentification: identification,
      };

      console.log("📤 Obteniendo info del cliente:", qry);

      webserver("get_client_info", qry, (response) => {
        console.log("📥 Respuesta get_client_info:", response);

        if (response && !response.error) {
          this.item = { ...createClient(), ...response };
          this.validateDocument();
        }
      });
    },

    // Guardar cliente
    saveClient() {
      if (!this.$refs.clientForm.validate()) {
        this.showError("Por favor complete todos los campos obligatorios");
        return;
      }

      if (!this.documentValidation.valid) {
        this.showError("Por favor valide el documento antes de guardar");
        return;
      }

      this.saving = true;
      const storeId = this.getStoreId();

      const qry = {
        store: storeId,
        party: {
          ...this.item,
          es_generico: this.item.es_generico ? 1 : 0,
        },
      };

      console.log("📤 Guardando cliente:", qry);

      webserver("put_client_info", qry, (response) => {
        this.saving = false;
        console.log("📥 Respuesta put_client_info:", response);

        if (response && response.success) {
          this.showSuccess(response.message);
          this.closeDialog();
          this.loadAllClients(); // Usar loadAllClients en lugar de searchClients
          this.getClientStats();
        } else if (response && response.error) {
          this.showError("Error al guardar: " + response.error);
        }
      });
    },

    // Utilidad para convertir es_generico a booleano
    isGenericClient(esGenerico) {
      return esGenerico === 1 || esGenerico === "1" || esGenerico === true;
    },

    // Eliminar cliente
    deleteClient(client) {
      if (this.isGenericClient(client.es_generico)) {
        this.showError("No se puede eliminar un cliente genérico");
        return;
      }

      this.confirmAction = {
        title: "Eliminar Cliente",
        message: `¿Está seguro de eliminar a ${client.PartyName}? Esta acción no se puede deshacer.`,
        icon: "mdi-delete",
        color: "error",
        confirmText: "Eliminar",
        action: "deleteClient",
        data: client,
      };

      this.confirmDialog = true;
    },

    executeConfirmAction() {
      if (this.confirmAction.action === "deleteClient") {
        this.executeDeleteClient();
      } else if (this.confirmAction.action === "loadExistingClient") {
        this.loadExistingClient(this.confirmAction.data);
        this.confirmDialog = false;
      }
    },

    executeDeleteClient() {
      const client = this.confirmAction.data;
      this.loading_status = true;
      const storeId = this.getStoreId();

      const qry = {
        store: storeId,
        PartyIdentification: client.PartyIdentification,
      };

      console.log("📤 Eliminando cliente:", qry);

      webserver("delete_client", qry, (response) => {
        this.loading_status = false;
        this.confirmDialog = false;
        console.log("📥 Respuesta delete_client:", response);

        if (response && response.success) {
          this.showSuccess(response.message);
          this.loadAllClients(); // Usar loadAllClients en lugar de searchClients
          this.getClientStats();
        } else if (response && response.error) {
          this.showError("Error al eliminar: " + response.error);
        }
      });
    },

    // Acciones UI
    newClient() {
      this.item = createClient();
      this.item.tipo_cliente = "identificado";
      this.item.channel = "POS"; // Canal por defecto
      // Establecer tipo de documento por defecto según país
      this.item.typeid = this.getDefaultDocumentTypeForCountry();
      this.item.es_generico = false;
      this.documentValidation = { valid: false, type: null, message: "" };
      this.newItem = true;
      this.dialog = true;
    },

    // Obtener tipo de documento por defecto para el país actual
    getDefaultDocumentTypeForCountry() {
      const countryCode = this.getCountryCode();
      const defaultTypes = {
        CO: "CC",
        ES: "DNI",
        MX: "CURP",
        AR: "DNI",
      };

      const defaultType = defaultTypes[countryCode] || "DNI";
      console.log(`🎯 Tipo por defecto para ${countryCode}: ${defaultType}`);
      return defaultType;
    },

    editClient(client) {
      if (this.isGenericClient(client.es_generico)) {
        this.showError("No se puede editar un cliente genérico");
        return;
      }

      this.item = { ...client };
      // Asegurar que channel tenga un valor por defecto si no existe
      if (!this.item.channel) {
        this.item.channel = "POS";
      }
      this.newItem = false;
      this.dialog = true;
      this.validateDocument();
    },

    closeDialog() {
      this.dialog = false;
      this.item = createClient();
      this.documentValidation = { valid: false, type: null, message: "" };
      this.$refs.clientForm?.resetValidation();
    },

    // Utilidades
    getClientTypeColor(type) {
      const colors = {
        identificado: "primary",
        contado: "orange",
        consumidor: "green",
        extranjero: "purple",
      };
      return colors[type] || "grey";
    },

    getClientTypeIcon(type) {
      const icons = {
        identificado: "mdi-account-check",
        contado: "mdi-cash",
        consumidor: "mdi-account",
        extranjero: "mdi-passport",
      };
      return icons[type] || "mdi-account";
    },

    getClientTypeText(type) {
      const types = {
        identificado: "Identificado",
        contado: "Contado",
        consumidor: "Consumidor",
        extranjero: "Extranjero",
      };
      return types[type] || type;
    },

    // Obtener color del tipo de documento según país
    getDocumentTypeColor(type) {
      const countryCode = this.getCountryCode();

      const colorsByCountry = {
        ES: {
          DNI: "primary",
          NIE: "orange",
          CIF: "purple",
          PAS: "green",
          VAT: "red",
        },
        CO: {
          CC: "blue",
          CE: "orange",
          NIT: "purple",
          PAS: "green",
        },
        MX: {
          CURP: "red",
          RFC: "purple",
          PAS: "green",
        },
        AR: {
          DNI: "blue",
          CUIL: "orange",
          CUIT: "purple",
          PAS: "green",
        },
      };

      const countryColors = colorsByCountry[countryCode] || colorsByCountry["ES"];
      return countryColors[type] || "grey";
    },

    // Obtener hint para documento usando countryUtils
    getDocumentHint() {
      const countryCode = this.getCountryCode();
      const docType = this.item.typeid;

      // Usar configuración de UI de countryUtils
      const uiConfig = countryUtils.getUIConfiguration(countryCode);

      const hints = {
        ES: {
          DNI: "Ej: 12345678Z (8 números + 1 letra)",
          NIE: "Ej: X1234567L (X/Y/Z + 7 números + 1 letra)",
          CIF: "Ej: A12345674 (1 letra + 7 números + 1 dígito)",
          PAS: "Ej: ESP123456 (3 letras + 6 números)",
          VAT: "Ej: ES12345678Z (código país + número)",
        },
        CO: {
          CC: "Ej: 1234567890 (6-10 números)",
          CE: "Ej: 1234567890 (6-10 números)",
          NIT: "Ej: 123456789-0 (9-10 números + dígito verificador)",
          PAS: "Ej: CO1234567 (2 letras + 6-9 números)",
        },
        MX: {
          CURP: "Ej: ABCD123456HDFABC01 (18 caracteres)",
          RFC: "Ej: ABC123456ABC (personas morales) o ABCD123456ABC (físicas)",
          PAS: "Ej: G12345678 (1 letra + 8 números)",
        },
        AR: {
          DNI: "Ej: 12345678 (7-8 números)",
          CUIL: "Ej: 20-12345678-9 (formato: XX-XXXXXXXX-X)",
          CUIT: "Ej: 30-12345678-9 (formato: XX-XXXXXXXX-X)",
          PAS: "Ej: ARG123456 (3 letras + 6 números)",
        },
      };

      const countryHints = hints[countryCode] || hints["ES"];
      return (
        countryHints[docType] ||
        uiConfig.placeholders.document ||
        "Ingrese el número de documento válido"
      );
    },

    // Obtener configuración del país actual usando countryUtils
    getCurrentCountryConfig() {
      const uiConfig = countryUtils.getUIConfiguration();
      return {
        ...uiConfig.country,
        code: this.getCountryCode(),
        name: uiConfig.country?.name || this.getCountryCode(),
        flag: this.getCountryFlag(),
      };
    },

    // Obtener bandera del país
    getCountryFlag() {
      const countryCode = this.getCountryCode();
      const flags = {
        ES: "🇪🇸",
        CO: "🇨🇴",
        MX: "🇲🇽",
        AR: "🇦🇷",
      };
      return flags[countryCode] || "🌍";
    },

    formatDate(dateString) {
      if (!dateString) return "N/A";
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString("es-ES", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
      } catch (e) {
        return "N/A";
      }
    },

    // Métodos para manejo de canales
    getChannelColor(channel) {
      const colors = {
        POS: "primary",
        WHATSAPP: "green",
        WEB: "blue",
        APP: "purple",
        INSTAGRAM: "pink",
        PHONE: "orange",
        EMAIL: "red",
        MARKETPLACE: "amber",
        IMPORT: "grey",
        API: "indigo",
      };
      return colors[channel] || "grey";
    },

    getChannelIcon(channel) {
      const icons = {
        POS: "mdi-point-of-sale",
        WHATSAPP: "mdi-whatsapp",
        WEB: "mdi-web",
        APP: "mdi-cellphone",
        INSTAGRAM: "mdi-instagram",
        PHONE: "mdi-phone",
        EMAIL: "mdi-email",
        MARKETPLACE: "mdi-shopping",
        IMPORT: "mdi-database-import",
        API: "mdi-api",
      };
      return icons[channel] || "mdi-help-circle";
    },

    getChannelText(channel) {
      const channelOption = this.channelOptions.find((opt) => opt.value === channel);
      return channelOption ? channelOption.text : channel || "Desconocido";
    },

    showSuccess(message) {
      // Manejo seguro de mensajes
      if (this.$store && this.$store.dispatch) {
        this.$store
          .dispatch("showMessage", {
            color: "success",
            message: message,
          })
          .catch(() => {
            // Si el store no tiene la acción, usar console
            console.log("✅ Éxito:", message);
          });
      } else {
        console.log("✅ Éxito:", message);
      }
    },

    showError(message) {
      // Manejo seguro de mensajes
      if (this.$store && this.$store.dispatch) {
        this.$store
          .dispatch("showMessage", {
            color: "error",
            message: message,
          })
          .catch(() => {
            // Si el store no tiene la acción, usar console
            console.error("❌ Error:", message);
          });
      } else {
        console.error("❌ Error:", message);
      }
    },
  },
};
</script>

<style scoped>
.kpi-card {
  transition: all 0.3s;
}

.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12) !important;
}

.table-cursor >>> tbody tr {
  cursor: pointer;
  transition: background-color 0.2s;
}

.table-cursor >>> tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

/* Indicador visual para filas clickeables */
.table-cursor >>> tbody tr:hover td:first-child::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background-color: var(--v-primary-base);
}

.v-data-table >>> .v-data-table__wrapper {
  border-radius: 8px;
}

.v-dialog .v-card {
  border-radius: 12px;
}

.v-dialog .v-card .v-card-title {
  border-radius: 12px 12px 0 0;
}
</style>
